import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { createFileRoute } from "@tanstack/react-router";
import { useId, useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CalendarIcon, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/guest")({
  head: () => ({
    meta: [
      { title: "Be a Guest on Figuring Out — Raj Shamani", key: "title" },
      { name: "description", content: "Apply to be a guest on Figuring Out, Raj Shamani's business podcast. Pitch your story for long-form conversations with India's leading entrepreneurs.", key: "description" },
      { property: "og:title", content: "Be a Guest on Figuring Out — Raj Shamani", key: "og:title" },
      { property: "og:description", content: "Apply to be a guest on Figuring Out, Raj Shamani's business podcast. Pitch your story for long-form conversations with India's leading entrepreneurs.", key: "og:description" },
      { property: "og:url", content: "https://rajshamani.life/guest", key: "og:url" },
      { property: "og:type", content: "website", key: "og:type" },
      { property: "og:site_name", content: "Raj Shamani", key: "og:site_name" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { property: "og:image", content: "https://rajshamani.life/__l5e/assets-v1/4d702d47-9f07-4958-aa4d-cec3235d2687/og-default.jpg", key: "og:image" },
      { property: "og:image:alt", content: "Be a guest on Figuring Out with Raj Shamani", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "twitter:title", content: "Be a Guest on Figuring Out — Raj Shamani", key: "twitter:title" },
      { name: "twitter:description", content: "Apply to be a guest on Figuring Out, Raj Shamani's business podcast. Pitch your story for long-form conversations with India's leading entrepreneurs.", key: "twitter:description" },
      { name: "twitter:image", content: "https://rajshamani.life/__l5e/assets-v1/4d702d47-9f07-4958-aa4d-cec3235d2687/og-default.jpg", key: "twitter:image" },
    ],
    links: [{ rel: "canonical", href: "https://rajshamani.life/guest" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Be a Guest on Figuring Out — Raj Shamani",
        headline: "Be a Guest on Figuring Out — Raj Shamani",
        description: "Apply to be a guest on Figuring Out, Raj Shamani's business podcast. Pitch your story for long-form conversations with India's leading entrepreneurs.",
        url: "https://rajshamani.life/guest",
        inLanguage: "en",
        isPartOf: { "@type": "WebSite", name: "Raj Shamani", url: "https://rajshamani.life/" },
        about: { "@type": "PodcastSeries", name: "Figuring Out", url: "https://rajshamani.life/podcast" },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: "https://rajshamani.life/__l5e/assets-v1/4d702d47-9f07-4958-aa4d-cec3235d2687/og-default.jpg",
        },
        image: "https://rajshamani.life/__l5e/assets-v1/4d702d47-9f07-4958-aa4d-cec3235d2687/og-default.jpg",
      }),
    }],
  }),
  component: Book,
});

const schema = z.object({
  name: z.string().trim().min(2).max(100),
  email: z.string().trim().email().max(255),
  company: z.string().trim().max(200).optional().or(z.literal("")),
  role: z.string().trim().max(200).optional().or(z.literal("")),
  topic: z.string().trim().min(5).max(300),
  bio: z.string().trim().min(30).max(2000),
  social_links: z.string().trim().max(1000).optional().or(z.literal("")),
  
});

function Book() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", role: "",
    topic: "", bio: "", social_links: "",
  });
  const [preferredDate, setPreferredDate] = useState<Date | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const bioId = useId();
  const dateId = useId();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.errors[0]?.message ?? "Please fill the form correctly.");
      return;
    }
    setLoading(true);
    const payload = {
      ...parsed.data,
      preferred_date: preferredDate ? format(preferredDate, "yyyy-MM-dd") : null,
    };
    const { error } = await supabase.from("podcast_bookings").insert(payload);
    setLoading(false);
    if (error) {
      toast.error("Something went wrong. Try again.");
      return;
    }
    setDone(true);
    toast.success("Pitch received!");
  }

  if (done) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-32 text-center">
        <CheckCircle2 className="mx-auto text-gold mb-6" size={56} />
        <h1 className="text-4xl font-bold mb-4">Pitch received.</h1>
        <p className="text-muted-foreground">
          We review every request personally. If it's a fit, the team will
          reach out within 7 days. Thanks for thinking of Figuring Out.
        </p>
      </div>
    );
  }

  return (
    <div>
      <PageBreadcrumb current="Be a Guest" />
      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-4xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4">Be a Guest</div>
          <h1 className="text-5xl md:text-6xl font-bold leading-tight">
            Pitch yourself for <span className="text-gradient-gold">Figuring Out.</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
            We look for sharp, unfiltered voices with a real story. Founders,
            operators, investors, athletes, artists — if you've built something
            worth talking about, we want to hear from you.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-24">
        <form onSubmit={submit} className="space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Full Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
            <Field label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
            <Field label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
            <Field label="Role / Title" value={form.role} onChange={(v) => setForm({ ...form, role: v })} />
          </div>
          <Field label="What do you want to talk about? *" value={form.topic} onChange={(v) => setForm({ ...form, topic: v })} required />
          <div>
            <label htmlFor={bioId} className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
              Short bio — who you are, what you've built *
            </label>
            <textarea
              id={bioId}
              required
              rows={5}
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              className="w-full px-4 py-3 rounded-md bg-input border border-border focus:outline-none focus:ring-1 focus:ring-gold resize-none"
            />
          </div>
          <Field label="Social links / website" value={form.social_links} onChange={(v) => setForm({ ...form, social_links: v })} placeholder="LinkedIn, Twitter, Instagram, website..." />
          <div>
            <label htmlFor={dateId} className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Preferred date (optional)</label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  id={dateId}
                  type="button"
                  aria-label="Select preferred date"
                  className={cn(
                    "w-full flex items-center gap-2 px-4 py-3 rounded-md bg-input border border-border focus:outline-none focus:ring-1 focus:ring-gold text-left",
                    !preferredDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon size={16} />
                  {preferredDate ? format(preferredDate, "PPP") : "Pick a date"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-popover" align="start">
                <Calendar
                  mode="single"
                  selected={preferredDate}
                  onSelect={setPreferredDate}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full px-8 py-4 rounded-md bg-gold text-primary-foreground font-medium hover:opacity-90 disabled:opacity-50 shadow-gold"
          >
            {loading ? "Sending..." : "Submit Pitch"}
          </button>
          <p className="text-xs text-muted-foreground text-center">
            We review every submission. Expect a response within 7 days if it's a fit.
          </p>
        </form>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean; placeholder?: string;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">{label}</label>
      <input
        id={id}
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 rounded-md bg-input border border-border focus:outline-none focus:ring-1 focus:ring-gold"
      />
    </div>
  );
}
